/**
 * The section-tab look, in one place.
 *
 * Underlined tabs sitting directly under a page title, used by any page whose content is split
 * into sections (Devices Order, OptiHub). Deliberately NOT the default pill `TabsList` style:
 * several of these pages have their own inner tab row (the coupon queue has Active / All /
 * Unpaid coupons), and two pill rows stacked read as one confused control rather than a hierarchy.
 *
 * Shared as strings rather than a component because the two callers use different primitives —
 * Devices Order routes with `NavLink`, OptiHub switches client-side with Radix `Tabs` — and the
 * point is that they LOOK identical, not that they work identically.
 */

/**
 * The row itself.
 *
 * Full-bleed bottom border with the tabs inset by the page gutter, so the rule runs edge to edge
 * under the title rather than stopping short of it. The `px-6` belongs HERE rather than in each
 * caller: it is what makes the rule line up with the page padding, and the two pages drifted
 * apart the first time it lived in their markup.
 */
export const SECTION_TAB_LIST =
  'flex h-auto w-full justify-start gap-1 rounded-none border-b bg-transparent px-6 py-0';

/** Every tab, active or not. */
export const SECTION_TAB_BASE =
  'inline-flex items-center gap-2 rounded-b-none rounded-t-md border-b-2 px-3 py-2 text-sm font-medium transition-colors';

/** Applied by the caller when it owns the active state (NavLink's isActive). */
export const SECTION_TAB_ACTIVE = 'border-primary text-foreground';
export const SECTION_TAB_IDLE =
  'border-transparent text-muted-foreground hover:border-muted hover:text-foreground';

/**
 * Radix drives its own active state via `data-state`, so the variants go in the class list rather
 * than being chosen in JS. `bg-transparent` + `shadow-none` undo the pill treatment the base
 * TabsTrigger applies when active.
 */
export const SECTION_TAB_RADIX = [
  SECTION_TAB_BASE,
  SECTION_TAB_IDLE,
  'data-[state=active]:border-primary data-[state=active]:bg-transparent',
  'data-[state=active]:text-foreground data-[state=active]:shadow-none',
].join(' ');
